import { BottomSheetModal } from '@gorhom/bottom-sheet';
import {
  FlexPayOverTimeIntroResultTypenames,
  type Mutation,
  type MutationSubmitFlexPayOverTimeOnboardingStepArgs,
  type Query,
  SubmitFlexPayOverTimeOnboardingResultTypenames,
} from '@varo/graphql-schema-types';
import { NativeStackHeaderProps, Stack, useRouter } from 'expo-router';
import LoadingSequence from 'features/payLater/components/LoadingSequence/LoadingSequence';
import PurchaseRestrictionsBottomSheet from 'features/payLater/components/PurchaseRestrictionsBottomSheet/PurchaseRestrictionsBottomSheet';
import { payLaterSetUpAtom } from 'features/payLater/store/payLaterSetUpStore';
import { useSetAtom } from 'jotai';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ScrollView, StyleSheet, View } from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import SubmitFlexPayOverTimeOnboardingStep from 'services/graphql/mutations/SubmitFlexPayOverTimeOnboardingStep';
import FetchFlexPayOverTimeIntroDetails from 'services/graphql/queries/FetchFlexPayOverTimeIntroDetails';
import Button from 'shared/components/vxl/Button/Button';
import VXLTextInput from 'shared/components/vxl/input/VXLTextInput/VXLTextInput';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import BodySmText from 'shared/components/vxl/typography/primary/body/BodySmText';
import HyperlinkSmText from 'shared/components/vxl/typography/primary/hyperlink/HyperlinkSmText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation/useCancellableMutation';
import useDismissToRootTab, {
  Tabs,
} from 'shared/hooks/useDismissToRootTab/useDismissToRootTab';
import ErrorScreen, {
  ErrorScreenType,
} from 'shared/patterns/vxl/errors/ErrorScreen/ErrorScreen';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import theme from 'shared/styles/theme';
import { useIdempotencyToken } from 'shared/utils/idempotencyTokens/idempotencyTokenUtils';
import {
  formatCurrency,
  formatCurrencyInput,
} from 'shared/utils/string/currency';
import { useQuery } from 'urql';

const USD = 'USD';
const PURCHASE_AMOUNT_INTENT = 'pay-later-purchase-amount';
const MINIMUM_PURCHASE_AMOUNT = 50;

const PurchaseAmount = () => {
  const router = useRouter();
  const { t: translatedText } = useTranslation('payLater', {
    keyPrefix: 'purchaseAmount',
  });
  const { bottom } = useSafeAreaInsets();
  const [amountText, setAmountText] = useState('');
  const [declineReason, setDeclineReason] = useState<string | null>(null);
  const restrictionsSheetRef = useRef<BottomSheetModal>(null);
  const setPayLaterSetUp = useSetAtom(payLaterSetUpAtom);
  const amountValue = Number(amountText.replace(/[$,]/g, ''));
  // Seeding with the amount rotates the token when the amount changes, so an
  // unknown outcome is only replayed for the same amount.
  const { idempotencyToken, rotateToken } = useIdempotencyToken(
    `${PURCHASE_AMOUNT_INTENT}:${amountValue}`,
  );
  const dismissToTab = useDismissToRootTab();
  const [{ data: introData, fetching: isIntroLoading }, reExecuteIntroQuery] =
    useQuery<Query>({
      query: FetchFlexPayOverTimeIntroDetails,
    });
  const [{ fetching: isSubmitting }, submitOnboardingStep] =
    useCancellableMutation<
      Mutation,
      MutationSubmitFlexPayOverTimeOnboardingStepArgs
    >(SubmitFlexPayOverTimeOnboardingStep);

  const introResult = introData?.flexPayOverTimeIntroDetails;
  const purchasingPower =
    introResult?.__typename ===
      FlexPayOverTimeIntroResultTypenames.FlexPayOverTimeIntroDetailsSuccess ||
    introResult?.__typename ===
      FlexPayOverTimeIntroResultTypenames.FlexPayOverTimeIntroNoneSuccess
      ? introResult.purchasingPowerAmount
      : null;
  const isAmountEntered = amountValue > 0;
  const isOverPurchasingPower = purchasingPower
    ? amountValue > Number(purchasingPower.amount)
    : false;
  const overPurchasingPowerText =
    purchasingPower && isOverPurchasingPower
      ? translatedText('overPurchasingPower', {
          amount: purchasingPower.formatted,
        })
      : undefined;
  const isUnderMinimum =
    isAmountEntered && amountValue < MINIMUM_PURCHASE_AMOUNT;
  const underMinimumText = isUnderMinimum
    ? translatedText('underMinimum', {
        amount: formatCurrency(MINIMUM_PURCHASE_AMOUNT, false),
      })
    : undefined;
  const amountErrorText =
    declineReason ?? overPurchasingPowerText ?? underMinimumText;
  const hasPurchasingPowerFailed = !isIntroLoading && !purchasingPower;
  const footerBottomPadding = bottom + theme.spacing.m;

  const handleAmountChange = (text: string) => {
    setAmountText(formatCurrencyInput(text));
    setDeclineReason(null);
  };

  const handleRetryPurchasingPowerPress = () => {
    reExecuteIntroQuery({ requestPolicy: 'network-only' });
  };

  const handleRestrictionsPress = () => {
    restrictionsSheetRef.current?.present();
  };

  const handleNextPress = async () => {
    const purchaseAmount = { amount: amountValue.toFixed(2), currency: USD };

    try {
      const result = await submitOnboardingStep({
        idempotencyId: idempotencyToken,
        input: { purchaseAmount },
      });
      const step = result.data?.submitFlexPayOverTimeOnboardingStep;

      switch (step?.__typename) {
        case SubmitFlexPayOverTimeOnboardingResultTypenames.SubmitFlexPayOverTimeOnboardingPaymentPlan:
          rotateToken();
          setPayLaterSetUp({ paymentPlan: step, purchaseAmount });
          router.push('/pay-later/review');
          return;
        case SubmitFlexPayOverTimeOnboardingResultTypenames.SubmitFlexPayOverTimeOnboardingApplicationDeclined:
          // TODO JT-163173: declineReason is copy only, so amount-limit declines
          // and application denials both show inline. Route denials to a denial
          // screen once the contract exposes a decline code.
          rotateToken();
          setDeclineReason(step.declineReason);
          return;
        case SubmitFlexPayOverTimeOnboardingResultTypenames.SubmitFlexPayOverTimeOnboardingError:
          rotateToken();
          router.push('/pay-later/set-up-error');
          return;
        default:
          router.push('/pay-later/set-up-error');
      }
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }
      router.push('/pay-later/set-up-error');
    }
  };

  return (
    <View style={styles.screen}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: '',
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <KeyboardAvoidingView behavior="padding" style={styles.keyboardAvoider}>
        <ScrollView
          contentContainerStyle={styles.content}
          keyboardShouldPersistTaps="handled"
        >
          <View style={styles.description}>
            <TitleLgText numberOfLines={0}>
              {translatedText('title')}
            </TitleLgText>
            <BodyBaseText numberOfLines={0}>
              {translatedText('body')}
            </BodyBaseText>
          </View>
          <VXLTextInput
            labelText={translatedText('amountLabel')}
            value={amountText}
            onChangeText={handleAmountChange}
            placeholder={translatedText('amountPlaceholder')}
            keyboardType="decimal-pad"
            autoFocus
            assistiveText={
              purchasingPower
                ? translatedText('purchasingPower', {
                    amount: purchasingPower.formatted,
                  })
                : undefined
            }
            errorText={amountErrorText}
          />
        </ScrollView>
        <View style={[styles.footer, { paddingBottom: footerBottomPadding }]}>
          <BodySmText numberOfLines={0} style={styles.restrictionsText}>
            {translatedText('restrictions')}{' '}
            <HyperlinkSmText onPress={handleRestrictionsPress}>
              {translatedText('restrictionsLink')}
            </HyperlinkSmText>
          </BodySmText>
          <Button
            label={translatedText('next')}
            onPress={handleNextPress}
            isLoading={isIntroLoading || isSubmitting}
            isDisabled={
              !purchasingPower ||
              !isAmountEntered ||
              isOverPurchasingPower ||
              isUnderMinimum
            }
            type="PRIMARY"
            width="FULL"
          />
        </View>
      </KeyboardAvoidingView>
      <ScreenTransitionOverlay
        isLoading={isSubmitting}
        LoadingComponent={<LoadingSequence />}
        ErrorComponent={
          hasPurchasingPowerFailed ? (
            <ErrorScreen
              screenType={ErrorScreenType.Error}
              messageHeadline={translatedText('purchasingPowerError.title')}
              messageBody={translatedText('purchasingPowerError.body')}
              primaryButton={{
                title: translatedText('purchasingPowerError.tryAgain'),
                onPress: handleRetryPurchasingPowerPress,
              }}
              secondaryButton={{
                title: translatedText('purchasingPowerError.done'),
                onPress: () => dismissToTab(Tabs.HOME),
              }}
            />
          ) : null
        }
      />
      <PurchaseRestrictionsBottomSheet ref={restrictionsSheetRef} />
    </View>
  );
};

const styles = StyleSheet.create({
  content: {
    gap: theme.spacing.xl,
    padding: theme.spacing.m,
  },
  description: { gap: theme.spacing.s },
  keyboardAvoider: { flex: 1 },
  footer: {
    gap: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
  restrictionsText: { color: theme.color.text.secondary },
  screen: { backgroundColor: theme.color.background.primary, flex: 1 },
});

export default PurchaseAmount;
